/**
 * scheduleUtils.ts — Shared utilities for schedule display data.
 *
 * DRY: These were duplicated across SchedulingScreen and HomeScheduledScreen.
 */
import type { EditableRetreatSchedule } from "@retreat/core";

/**
 * Format a timestamp to a short clock string: "9:00 AM", "1:44 PM".
 */
export function fmtClock(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Compute total duration of a schedule in seconds.
 */
export function totalSeconds(schedule: EditableRetreatSchedule): number {
  return schedule.days.reduce(
    (sum, day) =>
      sum + day.containers.reduce((s, c) => s + c.durationSeconds, 0),
    0,
  );
}

/**
 * Format a duration in seconds to a human label.
 * e.g. 7200 → "2 hours", 5400 → "1h 30m", 1200 → "20 minutes"
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${mins} minutes`;
  if (mins === 0) return `${hours} hour${hours > 1 ? "s" : ""}`;
  return `${hours}h ${mins}m`;
}

/**
 * Format a countdown from now to a target timestamp.
 * Returns { label, numeral } for display in HomeScheduledScreen.
 */
export function formatCountdown(targetMs: number): {
  label: string;
  numeral: string;
} {
  const delta = Math.max(0, targetMs - Date.now());
  const totalSec = Math.floor(delta / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  if (days > 0) {
    return {
      label: "begins in",
      numeral: `${days}d ${String(hours).padStart(2, "0")}h`,
    };
  }
  if (hours > 0) {
    return {
      label: "begins in",
      numeral: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    };
  }
  return {
    label: "begins in",
    numeral: `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
  };
}

/**
 * Format a Date to "Saturday · May 9" style for SchedulingScreen.
 */
export function formatDateLabel(d: Date): string {
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const month = d.toLocaleDateString(undefined, { month: "long" });
  const day = d.getDate();
  return `${weekday} · ${month} ${day}`;
}

/**
 * Format a Date's end time given a duration in seconds.
 */
export function formatEndTime(start: Date, durationSec: number): string {
  const end = new Date(start.getTime() + durationSec * 1000);
  let time = end.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  // Handle midnight: 24:00 → 00:00
  if (time === "24:00") {
    time = "00:00";
  }
  return time;
}

/**
 * Format a schedule's name for display.
 * Names are human-readable strings — return as-is.
 */
export function displayScheduleName(schedule: EditableRetreatSchedule): string {
  return schedule.name;
}

/**
 * Format a timestamp to date + time labels (used by MissedScreen, HomeScheduledScreen, WaitingScreen).
 * Returns { dateLabel: "Saturday, May 19", timeLabel: "09:30" }
 */
export function formatScheduleDateTime(ms: number): { dateLabel: string; timeLabel: string } {
  const d = new Date(ms);
  const dateLabel = d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const timeLabel = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { dateLabel, timeLabel };
}

/** Formats a timestamp as "9:30 AM" — 12h, no leading zero, short format. */
export function formatTime12h(ms: number): string {
  return new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}
